import { useIntl } from 'react-intl';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import type { SxProps, Theme } from '@mui/material/styles';
import { isStructureFinding, type FindingType } from '@ast-data/shared';

/**
 * The kind of Befund, as the heading of a Kenndaten box states it — on the
 * Befund form and on the Planum/Profil forms under it alike.
 *
 * Renders NOTHING for an ordinary Befundbeschreibung: a chip there would name
 * what every record already is. With `onChange` — the Befund form, for whoever
 * holds `update FindingType` — it shows either kind and switches it.
 */
export default function FindingTypeChip({
    findingType,
    onChange,
    sx
}: {
    /** Undefined while the owning Befund is still loading. */
    findingType: FindingType | undefined;
    onChange?: () => void;
    /**
     * Placement, for a caller that has to put the chip somewhere specific —
     * `ml: 'auto'` pushes it to the far end of a flex row. Nothing about the
     * chip's own looks belongs here.
     */
    sx?: SxProps<Theme>;
}) {
    const intl = useIntl();
    const isStructure = isStructureFinding(findingType);
    if (!onChange && !isStructure) {
        return null;
    }
    const chip = (
        <Chip
            size="small"
            color={isStructure ? 'info' : 'default'}
            variant="outlined"
            icon={onChange ? <SwapHorizIcon /> : undefined}
            label={intl.formatMessage({
                id: isStructure
                    ? 'finding.type.structure'
                    : 'finding.type.description'
            })}
            onClick={onChange}
            sx={sx}
        />
    );
    if (!onChange) {
        return chip;
    }
    return (
        // describeChild: the chip keeps the type as its name.
        <Tooltip
            describeChild
            title={intl.formatMessage({ id: 'finding.type.change.title' })}
        >
            {chip}
        </Tooltip>
    );
}
